"use client";

import { apiClient } from "./api";

// =============================================================================
// Types
// =============================================================================

export interface BaseColors {
  accent: string;
  gray: string;
  background: string;
}

export interface CSSVariables {
  light?: Record<string, string>;
  dark?: Record<string, string>;
}

export interface Features {
  tenantAdmin: boolean;
  deviceManagement: boolean;
  dashboards: boolean;
  widgets: boolean;
}

export interface SemanticColors {
  destructive: string;
  success: string;
  warning: string;
  info: string;
}

export interface ChartColors {
  chart1: string;
  chart2: string;
  chart3: string;
  chart4: string;
  chart5: string;
}

export interface BrandingConfig {
  id: string;
  name: string;
  shortName: string;
  description?: string;
  logoResourceKey?: string;
  logoPublicUrl?: string;
  faviconResourceKey?: string;
  faviconPublicUrl?: string;
  themeTemplateId?: string; // Reference to a built-in theme template
  customThemeId?: string; // Reference to a custom theme
  baseColors?: BaseColors;
  semanticColors?: SemanticColors;
  chartColors?: ChartColors;
  savedCssVariables?: CSSVariables;
  fullPalette?: Record<string, unknown>;
  customCss?: string;
  features?: Features;
  services?: Record<string, unknown>;
  entityType?: "system" | "integrator" | "organization";
  entityId?: string | null;
  entityName?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateBrandingConfigRequest {
  name?: string;
  shortName?: string;
  description?: string;
  logoResourceKey?: string;
  faviconResourceKey?: string;
  themeTemplateId?: string; // Reference to a built-in theme template
  customThemeId?: string; // Reference to a custom theme
  baseColors?: BaseColors;
  semanticColors?: SemanticColors;
  chartColors?: ChartColors;
  savedCssVariables?: CSSVariables;
  fullPalette?: Record<string, unknown>;
  customCss?: string;
  features?: Features;
  services?: Record<string, unknown>;
}

export interface CreateBrandingConfigRequest {
  name?: string;
  shortName?: string;
  description?: string;
  logoResourceKey?: string;
  faviconResourceKey?: string;
  themeTemplateId?: string; // Reference to a built-in theme template
  customThemeId?: string; // Reference to a custom theme
  baseColors?: BaseColors;
  semanticColors?: SemanticColors;
  chartColors?: ChartColors;
  savedCssVariables?: CSSVariables;
  fullPalette?: Record<string, unknown>;
  customCss?: string;
  features?: Features;
  services?: Record<string, unknown>;
  // Entity assignment - if provided, assigns branding to this entity
  // and derives name/shortName from entity if not provided
  entityType?: "system" | "integrator" | "organization";
  entityId?: string;
}

/**
 * List view item for branding configurations
 * Includes derived entity information and counts
 */
export interface BrandingConfigListItem {
  id: string;
  name: string;
  entityType: "system" | "integrator" | "organization";
  entityId: string | null;
  entityName: string;
  logoPublicUrl?: string;
  accentColor?: string;
  domainCount: number;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// API Client
// =============================================================================

/**
 * Branding API client for the v2 branding configuration system
 */
export const brandingApi = {
  /**
   * Get the current user's branding configuration
   * Returns integrator-specific branding or platform default
   */
  async getConfig(): Promise<BrandingConfig> {
    const response = await apiClient.get<BrandingConfig>("/v1/branding");
    return response.data;
  },

  /**
   * Get branding configuration for a specific integrator
   * Use 'platform' for the platform default
   * @param integratorId Integrator UUID or 'platform'
   */
  async getConfigByIntegrator(integratorId: string): Promise<BrandingConfig> {
    const response = await apiClient.get<BrandingConfig>(
      `/v1/branding/${integratorId}`
    );
    return response.data;
  },

  /**
   * Create branding configuration for an integrator
   * @param request Create branding config request
   */
  async create(request: CreateBrandingConfigRequest): Promise<BrandingConfig> {
    const response = await apiClient.post<BrandingConfig>(
      "/v1/branding",
      request
    );
    return response.data;
  },

  /**
   * Update branding configuration
   * @param integratorId Integrator UUID or 'platform'
   * @param updates Updates to apply
   */
  async update(
    integratorId: string,
    updates: UpdateBrandingConfigRequest
  ): Promise<BrandingConfig> {
    const response = await apiClient.put<BrandingConfig>(
      `/v1/branding/${integratorId}`,
      updates
    );
    return response.data;
  },

  /**
   * Delete integrator branding configuration (reverts to platform default)
   * @param integratorId Integrator UUID
   */
  async delete(integratorId: string): Promise<void> {
    await apiClient.delete(`/v1/branding/${integratorId}`);
  },

  /**
   * List all branding configurations (admin only)
   * @deprecated Use listConfigs() instead for list view with entity info
   */
  async listAll(): Promise<BrandingConfig[]> {
    const response = await apiClient.get<BrandingConfig[]>(
      "/v1/admin/branding"
    );
    return response.data;
  },

  /**
   * List all branding configurations with entity information
   * Returns list items with derived entity names and domain counts
   */
  async listConfigs(): Promise<BrandingConfigListItem[]> {
    const response = await apiClient.get<BrandingConfigListItem[]>(
      "/v1/branding/configs"
    );
    return response.data;
  },

  // =============================================================================
  // New Architecture Methods (v2 - using branding_config.id references)
  // =============================================================================

  /**
   * Get branding configuration by its ID
   * @param configId Branding config UUID
   */
  async getConfigById(configId: string): Promise<BrandingConfig> {
    const response = await apiClient.get<BrandingConfig>(
      `/v1/branding/config/${configId}`
    );
    return response.data;
  },

  /**
   * Get branding configuration for a domain
   * Performs domain lookup: domain -> integrator/org -> branding_config
   * @param domain Domain to look up (e.g., "customer.example.com")
   */
  async getConfigByDomain(domain: string): Promise<BrandingConfig> {
    const response = await apiClient.get<BrandingConfig>(
      `/v1/branding/domain/${domain}`
    );
    return response.data;
  },

  /**
   * Assign a branding configuration to an integrator
   * @param integratorId Integrator UUID
   * @param brandingConfigId Branding config UUID to assign
   */
  async assignToIntegrator(
    integratorId: string,
    brandingConfigId: string
  ): Promise<void> {
    await apiClient.put(`/v1/admin/integrators/${integratorId}/branding`, {
      brandingConfigId,
    });
  },

  /**
   * Assign a branding configuration override to an organization
   * Pass null/empty to remove the override (falls back to integrator branding)
   * @param organizationId Organization UUID
   * @param brandingConfigId Branding config UUID to assign, or null to remove override
   */
  async assignToOrganization(
    organizationId: string,
    brandingConfigId: string | null
  ): Promise<void> {
    await apiClient.put(`/v1/admin/organizations/${organizationId}/branding`, {
      brandingConfigId,
    });
  },

  /**
   * Remove branding override from an organization (reverts to integrator branding)
   * @param organizationId Organization UUID
   */
  async removeOrganizationOverride(organizationId: string): Promise<void> {
    await this.assignToOrganization(organizationId, null);
  },

  // =============================================================================
  // Convenience Methods
  // =============================================================================

  /**
   * Update just the brand colors
   * @param integratorId Integrator UUID or 'platform'
   * @param colors Base colors to update
   */
  async updateColors(
    integratorId: string,
    colors: BaseColors
  ): Promise<BrandingConfig> {
    return this.update(integratorId, { baseColors: colors });
  },

  /**
   * Update the logo
   * @param integratorId Integrator UUID or 'platform'
   * @param logoResourceKey Resource key of the uploaded logo image
   */
  async updateLogo(
    integratorId: string,
    logoResourceKey: string
  ): Promise<BrandingConfig> {
    return this.update(integratorId, { logoResourceKey });
  },

  /**
   * Update the favicon
   * @param integratorId Integrator UUID or 'platform'
   * @param faviconResourceKey Resource key of the uploaded favicon image
   */
  async updateFavicon(
    integratorId: string,
    faviconResourceKey: string
  ): Promise<BrandingConfig> {
    return this.update(integratorId, { faviconResourceKey });
  },

  /**
   * Update CSS variables (generated by theme editor)
   * @param integratorId Integrator UUID or 'platform'
   * @param cssVariables CSS variables for light and dark themes
   */
  async updateCSSVariables(
    integratorId: string,
    cssVariables: CSSVariables
  ): Promise<BrandingConfig> {
    return this.update(integratorId, { savedCssVariables: cssVariables });
  },

  /**
   * Update the theme template ID (for built-in themes)
   * When set, the template CSS is looked up at render time instead of using savedCssVariables
   * @param integratorId Integrator UUID or 'platform'
   * @param themeTemplateId Built-in theme template ID (e.g., "ocean-breeze", "modern-minimal")
   */
  async updateThemeTemplate(
    integratorId: string,
    themeTemplateId: string
  ): Promise<BrandingConfig> {
    return this.update(integratorId, { themeTemplateId });
  },

  /**
   * Update feature flags
   * @param integratorId Integrator UUID or 'platform'
   * @param features Feature flags to update
   */
  async updateFeatures(
    integratorId: string,
    features: Partial<Features>
  ): Promise<BrandingConfig> {
    return this.update(integratorId, { features: features as Features });
  },

  /**
   * Update the full theme (colors + CSS variables + palette)
   * @param integratorId Integrator UUID or 'platform'
   * @param theme Theme data from Enhanced Theme Editor
   */
  async updateTheme(
    integratorId: string,
    theme: {
      baseColors: BaseColors;
      savedCssVariables?: CSSVariables;
      fullPalette?: Record<string, unknown>;
    }
  ): Promise<BrandingConfig> {
    return this.update(integratorId, {
      baseColors: theme.baseColors,
      savedCssVariables: theme.savedCssVariables,
      fullPalette: theme.fullPalette,
    });
  },
};

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Default Techquity base colors
 */
export const DEFAULT_BASE_COLORS: BaseColors = {
  accent: "#EE244A",
  gray: "#6b7280",
  background: "#f8fafc",
};

/**
 * Default feature flags
 */
export const DEFAULT_FEATURES: Features = {
  tenantAdmin: true,
  deviceManagement: true,
  dashboards: true,
  widgets: true,
};

/**
 * Default semantic colors
 */
export const DEFAULT_SEMANTIC_COLORS: SemanticColors = {
  destructive: "#dc2626",
  success: "#16a34a",
  warning: "#ca8a04",
  info: "#2563eb",
};

/**
 * Default chart colors
 */
export const DEFAULT_CHART_COLORS: ChartColors = {
  chart1: "#3b82f6",
  chart2: "#22c55e",
  chart3: "#f59e0b",
  chart4: "#ef4444",
  chart5: "#8b5cf6",
};

/**
 * Check if branding is using default/platform colors
 */
export function isDefaultBranding(config: BrandingConfig): boolean {
  if (!config.baseColors) return true;
  return (
    config.baseColors.accent === DEFAULT_BASE_COLORS.accent &&
    config.baseColors.gray === DEFAULT_BASE_COLORS.gray &&
    config.baseColors.background === DEFAULT_BASE_COLORS.background
  );
}

/**
 * Convert BrandingConfig to the format expected by brand-config.ts
 */
export function toBrandConfig(config: BrandingConfig) {
  return {
    name: config.name,
    shortName: config.shortName,
    description: config.description || "",
    logoUrl: config.logoPublicUrl || "/logo.svg",
    faviconUrl: config.faviconPublicUrl || "/favicon.ico",
    baseColors: config.baseColors || DEFAULT_BASE_COLORS,
    semanticColors: config.semanticColors || DEFAULT_SEMANTIC_COLORS,
    chartColors: config.chartColors || DEFAULT_CHART_COLORS,
    savedCssVariables: config.savedCssVariables,
    features: config.features || DEFAULT_FEATURES,
    customThemeUrl: config.customCss ? undefined : undefined, // Custom CSS is inline
  };
}
